// Pakettransport (Welle 6, Vertrag W6.3/W6.5): Der Vergleich je Werk liefert `transportMode`
// ("package" | "artifact") und `designArtifacts` (alle Objekte außer iFlows, mit Typ).
// Bei "package" transportiert der Snapshot das ganze SAP-Paket; die Auswahl einzelner Zeilen entfällt.
// Fehlt `transportMode` (älterer Server oder Notschalter), gilt das bisherige Verhalten je Artefakt.

import { isArtifactDraft } from "./sameVersion";

const entriesOf = draft => Object.values(draft?.precheckByBucket || {})
  .filter(entry => entry && !entry.error && Array.isArray(entry.comparison));

/** "package", "artifact" oder null, wenn der Server keine Transportart liefert. */
export function packageTransportMode(draft) {
  const entries = entriesOf(draft);
  if (entries.some(entry => entry.transportMode === "package")) return "package";
  if (entries.some(entry => entry.transportMode === "artifact")) return "artifact";
  return null;
}

export const isPackageTransport = draft => packageTransportMode(draft) === "package";

/**
 * Freigegebene iFlow-IDs des Pakets aus dem Vergleich, ohne Entwürfe, wie auf den bisherigen Wegen. Der Server
 * erhält die Entwürfe trotzdem vollständig, denn der Paketexport enthält das ganze Paket; die Liste nennt nur,
 * was als Artefakt nachgewiesen und deployt werden kann.
 */
export function packageIflowIds(draft) {
  const ids = [];
  for (const entry of entriesOf(draft)) for (const artifact of entry.comparison) {
    if (isArtifactDraft(artifact)) continue;
    const id = artifact?.originalId || artifact?.Id;
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

// SAP-Typschlüssel aus dem Export (resourceType) als lesbarer Fachbegriff.
const TYPE_LABELS = {
  messagemapping: "Message Mapping", valuemapping: "Value Mapping", scriptcollection: "Script Collection",
  datatype: "Data Type", messagetype: "Message Type", serviceinterface: "Service Interface",
  functionlibrary: "Function Library", integrationadapter: "Integration Adapter", imported_archive: "Imported Archive",
  file: "File", url: "URL", odataservice: "OData Service", restapi: "REST API", soapapi: "SOAP API",
};

export function designTypeLabel(type) {
  const raw = String(type || "").trim();
  if (!raw) return "";
  const known = TYPE_LABELS[raw.replace(/\s+/g, "").toLowerCase()];
  if (known) return known;
  return /\s/.test(raw) ? raw : raw.replace(/([a-z])([A-Z])/g, "$1 $2");
}

const text = value => (typeof value === "string" && value.trim() ? value : null);

// In der Integration Suite deploybare Designobjekte; Data Types, Message Types und Service Interfaces sind es nicht.
const DEPLOYABLE = new Set(["messagemapping", "scriptcollection", "valuemapping"]);

/**
 * Designobjekte des Pakets, zusammengeführt über alle Werke:
 * `[{ id, name, type, byWerk: { [werkKey]: { targetName, targetId } } }]`.
 * Zielname und Ziel-ID kommen ausschließlich vom Server (`targetName`, `targetId` bzw. `suffixedId`).
 * Ohne Werk gilt der Quellname, denn ohne Kennung wird nichts umbenannt.
 */
export function packageDesignObjects(draft) {
  const byId = new Map();
  for (const entry of entriesOf(draft)) {
    const werkKey = entry.werk?.kennung || "std";
    for (const item of Array.isArray(entry.designArtifacts) ? entry.designArtifacts : []) {
      const id = text(item?.id) || text(item?.Id);
      // Kind-Ressourcen (etwa aufgelöste Service Interfaces) reisen mit ihrem Objekt, eine eigene Zeile hätten sie nicht.
      if (!id || item.kind === "child") continue;
      const name = text(item.name) || text(item.Name) || id;
      if (!byId.has(id)) byId.set(id, { id, name, type: designTypeLabel(item.type || item.Type), rawType: item.type || item.Type || "", byWerk: {},
        ...(text(item.version) || text(item.Version) ? { version: text(item.version) || text(item.Version) } : {}),
        deployable: DEPLOYABLE.has(String(item.type || item.Type || "").replace(/\s+/g, "").toLowerCase()),
        ...(item.withoutManifest === true ? { withoutManifest: true } : {}) });
      const own = werkKey !== "std";
      byId.get(id).byWerk[werkKey] = {
        targetName: text(item.targetName) || (own ? null : name),
        targetId: text(item.targetId) || text(item.suffixedId) || (own ? null : id),
        // Stand gegen das Ziel, soweit der Server ihn liefert (new, update, identical) samt Zielversion.
        ...(text(item.status) ? { status: item.status } : {}),
        ...(text(item.targetVersion) ? { targetVersion: item.targetVersion } : {}),
        // DEV181 C1: Servertext zur Ziel-ID in einem anderen Paket.
        ...(text(item.conflict?.message) ? { conflict: item.conflict.message } : {}),
      };
    }
  }
  return [...byId.values()].sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name));
}
